import { useState, useEffect } from 'react';
import api from '../utils/api';

/**
 * Cascading Country → State → City selector.
 * Props: country, state, city, onChange({ country, state, city })
 * Allows typing new values or selecting from existing.
 */
export function LocationPicker({ country = '', state = '', city = '', onChange, compact = false }) {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    api.get('/locations/countries').then(r => setCountries(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (country) {
      api.get(`/locations/states?country=${encodeURIComponent(country)}`).then(r => setStates(r.data || [])).catch(() => {});
    } else {
      setStates([]);
    }
  }, [country]);

  useEffect(() => {
    if (country && state) {
      api.get(`/locations/cities?country=${encodeURIComponent(country)}&state=${encodeURIComponent(state)}`).then(r => setCities(r.data || [])).catch(() => {});
    } else {
      setCities([]);
    }
  }, [country, state]);

  const h = compact ? 'h-8' : 'h-10';
  const cls = `w-full ${h} rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all`;

  return (
    <div className={`grid ${compact ? 'grid-cols-3 gap-2' : 'grid-cols-1 sm:grid-cols-3 gap-3'}`} data-testid="location-picker">
      <div>
        {!compact && <label className="block text-sm font-medium text-slate-700 mb-1.5">Country</label>}
        <input
          list="lp-countries"
          value={country}
          onChange={e => onChange({ country: e.target.value, state: '', city: '' })}
          placeholder={compact ? 'Country' : 'Select or type country'}
          className={cls}
          data-testid="location-country"
        />
        <datalist id="lp-countries">
          {countries.map(c => <option key={c} value={c} />)}
        </datalist>
      </div>
      <div>
        {!compact && <label className="block text-sm font-medium text-slate-700 mb-1.5">State</label>}
        <input
          list="lp-states"
          value={state}
          onChange={e => onChange({ country, state: e.target.value, city: '' })}
          placeholder={compact ? 'State' : 'Select or type state'}
          className={cls}
          data-testid="location-state"
        />
        <datalist id="lp-states">
          {states.map(s => <option key={s} value={s} />)}
        </datalist>
      </div>
      <div>
        {!compact && <label className="block text-sm font-medium text-slate-700 mb-1.5">City</label>}
        <input
          list="lp-cities"
          value={city}
          onChange={e => onChange({ country, state, city: e.target.value })}
          placeholder={compact ? 'City' : 'Select or type city'}
          className={cls}
          data-testid="location-city"
        />
        <datalist id="lp-cities">
          {cities.map(c => <option key={c} value={c} />)}
        </datalist>
      </div>
    </div>
  );
}
